import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { ArchivosUploadArea } from './ArchivosUploadArea';
import { ArchivosList } from './ArchivosList';
import { useArchivos } from '../hooks/useArchivos';
import type { OtroTramiteRecord } from '../types';
import toast from 'react-hot-toast';
import { useAuthorization } from '@/auth/hooks/useAuthorization';
import type { ArchivoRecord } from '../types';
import WhatsAppOtroTramiteArchivoModal from '@/components/WhatsAppOtroTramiteArchivoModal';
import WhatsAppOtroTramiteFacturaModal from '@/components/WhatsAppOtroTramiteFacturaModal';

interface ArchivosDialogProps {
  tramite: OtroTramiteRecord | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  scope?: 'all' | 'factura' | 'otros';
}

export const ArchivosDialog: React.FC<ArchivosDialogProps> = ({
  tramite,
  open,
  onOpenChange,
  scope = 'all',
}) => {
  const { canSendWhatsApp } = useAuthorization();
  const [selectedArchivoForWhatsApp, setSelectedArchivoForWhatsApp] = React.useState<ArchivoRecord | null>(null);
  const otroTramiteId = tramite?.id ?? '';

  const isFacturaScope = scope === 'factura';
  const titleLabel = isFacturaScope ? 'Facturas' : 'Archivos';
  const itemLabel = isFacturaScope ? 'factura' : 'archivo';
  const emptyTitle = isFacturaScope ? 'No hay facturas' : 'No hay archivos';
  const emptyDescription = isFacturaScope
    ? 'Sube la primera factura para este trámite'
    : 'Sube el primer archivo para este trámite';

  const {
    archivos,
    isLoading,
    isUploading,
    isDeleting,
    uploadArchivo,
    deleteArchivo,
    downloadArchivo,
  } = useArchivos({
    otroTramiteId,
    scope,
    tipoUpload: isFacturaScope ? 'factura' : undefined,
  });

  if (!tramite) return null;

  const handleUpload = async (file: File) => {
    try {
      await uploadArchivo(file);
      toast.success('Archivo subido correctamente');
    } catch (error: any) {
      toast.error(error.message || 'Error al subir el archivo');
    }
  };

  const handleDelete = async (archivoId: number) => {
    try {
      await deleteArchivo(archivoId);
      toast.success('Archivo eliminado correctamente');
    } catch (error) {
      toast.error('Error al eliminar el archivo');
    }
  };

  const handleDownload = async (archivo: any) => {
    try {
      await downloadArchivo(archivo);
    } catch (error) {
      toast.error('Error al descargar el archivo');
    }
  };

  const handleSendWhatsApp = (archivo: ArchivoRecord) => {
    setSelectedArchivoForWhatsApp(archivo);
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-[80vw] max-h-[80dvh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{titleLabel} de Trámite {tramite.id}</DialogTitle>
            <DialogDescription>
              {isFacturaScope
                ? 'Gestiona las facturas adjuntas a este trámite'
                : 'Gestiona los archivos adjuntos a este trámite'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 py-4">
            {/* Upload Area */}
            <ArchivosUploadArea onUpload={handleUpload} isUploading={isUploading} itemLabel={itemLabel} />

            {/* Files List */}
            <ArchivosList
              archivos={archivos}
              isLoading={isLoading}
              isDeleting={isDeleting}
              onDelete={handleDelete}
              onDownload={handleDownload}
              onSendWhatsApp={handleSendWhatsApp}
              canSendWhatsApp={canSendWhatsApp}
              title={titleLabel}
              emptyTitle={emptyTitle}
              emptyDescription={emptyDescription}
            />
          </div>
        </DialogContent>
      </Dialog>

      {selectedArchivoForWhatsApp && scope === 'factura' && (
        <WhatsAppOtroTramiteFacturaModal
          open={Boolean(selectedArchivoForWhatsApp)}
          onOpenChange={(nextOpen) => {
            if (!nextOpen) setSelectedArchivoForWhatsApp(null);
          }}
          tramite={tramite}
          archivo={selectedArchivoForWhatsApp}
        />
      )}

      {selectedArchivoForWhatsApp && scope !== 'factura' && (
        <WhatsAppOtroTramiteArchivoModal
          open={Boolean(selectedArchivoForWhatsApp)}
          onOpenChange={(nextOpen) => {
            if (!nextOpen) setSelectedArchivoForWhatsApp(null);
          }}
          tramite={tramite}
          archivo={selectedArchivoForWhatsApp}
        />
      )}
    </>
  );
};
